import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { ClickButton } from "../ClickButton";
import { FooterClickAction } from "../FooterAction";
import { Icon, type IconName } from "../Icon";
import { Keycap } from "../Keycap";
import { KeyboardHint } from "../KeyboardHint";
import { Text } from "../Text";
import { mobileMedia } from "../../hooks/useIsMobile";
import { maybeDisplayBrowserNotification } from "../../lib/browserNotifications";
import { isEditableTarget } from "../../lib/isEditableTarget";

const PromptContainer = styled("div")`
  border-radius: 12px;
  outline: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  padding: 4px;
`;

const PromptBody = styled("div")`
  display: flex;
  flex-direction: column;
  padding-bottom: 4px;
  gap: 4px;
`;

const PromptHeader = styled("div")`
  display: flex;
  padding: 10px 8px;
  box-sizing: content-box;
  align-items: baseline;
  flex-direction: column;
  gap: 4px;
  color: var(--text-secondary);
`;

const Row = styled(ClickButton)`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  padding-right: 28px;
  cursor: pointer;
  border-radius: 8px;
  position: relative;
  text-align: left;
  width: 100%;
  box-sizing: border-box;
  &[data-highlighted="true"] {
    background: var(--alpha4);
  }
`;

const ActionIcon = styled("span")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const Footer = styled("div")`
  display: flex;
  align-items: center;
  margin: 0 -4px -4px -4px;
  height: 40px;
  padding: 0 12px;
  border-top: 0.5px solid var(--border-alpha);
  justify-content: space-between;
  ${mobileMedia} {
    padding: 0 8px;
  }
`;

const Instructions = styled("div")`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  gap: 4px;
`;

export function ChatActionPrompt({
  title,
  description,
  notification = title,
  confirmLabel,
  confirmIcon,
  cancelLabel,
  cancelIcon,
  disabled = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  description?: string;
  notification?: string;
  confirmLabel: string;
  confirmIcon?: IconName;
  cancelLabel: string;
  cancelIcon?: IconName;
  disabled?: boolean;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const promptRef = useRef<HTMLDivElement>(null);

  const select = () => {
    if (disabled) return;
    if (selectedIndex === 0) {
      if (!confirmDisabled) onConfirm();
    } else onCancel();
  };

  useEffect(() => {
    maybeDisplayBrowserNotification("3B needs you", notification);
  }, [notification]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        disabled ||
        e.defaultPrevented ||
        e.isComposing ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey
      )
        return;
      if (
        e.key === "Enter" &&
        e.target instanceof Element &&
        e.target.closest("button")
      )
        return;
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
        return;
      }
      if (isEditableTarget(e)) return;
      if (e.key === "1") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex(0);
        if (!confirmDisabled) onConfirm();
        return;
      }
      if (e.key === "2") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex(1);
        onCancel();
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        const index = e.key === "ArrowDown" || confirmDisabled ? 1 : 0;
        setSelectedIndex(index);
        promptRef.current
          ?.querySelectorAll<HTMLButtonElement>("[data-highlighted]")
          [index]?.focus({ preventScroll: true });
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        if (selectedIndex === 0) {
          if (!confirmDisabled) onConfirm();
        } else onCancel();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onConfirm, onCancel, selectedIndex, disabled, confirmDisabled]);

  return (
    <PromptContainer
      ref={promptRef}
      role="group"
      aria-label={title}
      aria-busy={disabled}
    >
      <PromptBody>
        <PromptHeader>
          <Text variant="text-small">{title}</Text>
          {description && (
            <Text variant="text-smaller-md" color="var(--text-tertiary)">
              {description}
            </Text>
          )}
        </PromptHeader>
        <Row
          data-no-pill
          disabled={disabled || confirmDisabled}
          onMouseEnter={() => setSelectedIndex(0)}
          onFocus={() => setSelectedIndex(0)}
          onClick={onConfirm}
          data-highlighted={selectedIndex === 0}
        >
          {confirmIcon ? (
            <ActionIcon>
              <Icon name={confirmIcon} size={16} color="var(--text-primary)" />
            </ActionIcon>
          ) : (
            <Keycap
              aria-hidden
              style={{ width: 16, height: 16, padding: 0, flexShrink: 0 }}
            >
              <Text variant="text-small-md">1</Text>
            </Keycap>
          )}
          <Text variant="text-regular" color="var(--text-primary)">
            {confirmLabel}
          </Text>
          {selectedIndex === 0 && !disabled && !confirmDisabled && (
            <KeyboardHint style={{ position: "absolute", right: 8, top: 8 }}>
              <Keycap>
                <Icon name="corner-down-left" size={12} />
              </Keycap>
            </KeyboardHint>
          )}
        </Row>
        <Row
          data-no-pill
          disabled={disabled}
          onMouseEnter={() => setSelectedIndex(1)}
          onFocus={() => setSelectedIndex(1)}
          onClick={onCancel}
          data-highlighted={selectedIndex === 1}
        >
          {cancelIcon ? (
            <ActionIcon>
              <Icon name={cancelIcon} size={16} color="var(--text-primary)" />
            </ActionIcon>
          ) : (
            <Keycap
              aria-hidden
              style={{ width: 16, height: 16, padding: 0, flexShrink: 0 }}
            >
              <Text variant="text-small-md">2</Text>
            </Keycap>
          )}
          <Text variant="text-regular" color="var(--text-primary)">
            {cancelLabel}
          </Text>
          {selectedIndex === 1 && !disabled && (
            <KeyboardHint style={{ position: "absolute", right: 8, top: 8 }}>
              <Keycap>
                <Icon name="corner-down-left" size={12} />
              </Keycap>
            </KeyboardHint>
          )}
        </Row>
      </PromptBody>
      <Footer>
        <Instructions>
          <FooterClickAction disabled={disabled} onClick={onCancel}>
            <Text variant="text-smaller-md">{cancelLabel}</Text>
            <KeyboardHint>
              <Keycap style={{ padding: "2px 4px" }}>
                <Text variant="text-smaller-md">esc</Text>
              </Keycap>
            </KeyboardHint>
          </FooterClickAction>
        </Instructions>
        <Instructions>
          <FooterClickAction
            disabled={disabled || (selectedIndex === 0 && confirmDisabled)}
            onClick={select}
          >
            <Text variant="text-smaller-md">Select</Text>
            <KeyboardHint>
              <Keycap>
                <Icon name="corner-down-left" size={12} />
              </Keycap>
            </KeyboardHint>
          </FooterClickAction>
        </Instructions>
      </Footer>
    </PromptContainer>
  );
}
